import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { COMPANY_API_SERVICE_TOKEN, PERSON_API_SERVICE_TOKEN } from '@nk/api-lib';
import { PrettyCompanyNamePipe } from '@nk/company-block';
import { CompanyEntity, CompanyRoleEntity, PersonCompanyRolePersonFlagCompositeEntity } from '@nk/data-entities-lib';
import { PersonTitleComponent } from '@nk/person-block';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';

interface CompanyRoleByRank {
  companyRole: CompanyRoleEntity;
  entities: PersonCompanyRolePersonFlagCompositeEntity[];
}

function reduceCompanyRolesByRank(entities: PersonCompanyRolePersonFlagCompositeEntity[]): CompanyRoleByRank[] {
  const reducedByRank = entities.reduce(
    (
      res: {
        [companyRoleRank: number]: CompanyRoleByRank;
      },
      i,
    ) => {
      if (!res[i.company_role.company_role_rank]) {
        res[i.company_role.company_role_rank] = {
          companyRole: i.company_role,
          entities: [],
        };
      }
      res[i.company_role.company_role_rank].entities.push(i);
      return res;
    },
    {},
  );
  return Object.values(reducedByRank);
}
function sortCompanyRolesByRank(entities: CompanyRoleByRank[]): CompanyRoleByRank[] {
  return entities.sort((a, b) => a.companyRole.company_role_rank - b.companyRole.company_role_rank);
}

@Component({
  selector: 'nk-screening-report-company-roles',
  imports: [AsyncPipe, TranslateModule, PersonTitleComponent, PrettyCompanyNamePipe],
  templateUrl: './screening-report-company-roles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningReportCompanyRolesComponent {
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  public company$!: Observable<CompanyEntity>;
  public personCompanyRolePersonFlagCompositeEntities$: Observable<CompanyRoleByRank[]> | undefined;
  private _companyService = inject(COMPANY_API_SERVICE_TOKEN);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _companyRolesLoadingId = computed(() => `${this.constructor.name}_companyRoles`);
  private _companyLoadingId = computed(() => `${this.constructor.name}_company`);

  constructor() {
    effect(() => {
      if (this._reportLoadingStateService) {
        this._reportLoadingStateService.setLoadingState(this._companyRolesLoadingId(), true);
      }
      const companyUuid = this._screeningInputParamsService.screeningInputParams()?.companyUuid;
      if (companyUuid) {
        this.personCompanyRolePersonFlagCompositeEntities$ =
          this._personService.personCompanyRolePersonFlagListByCompanyCache.get(companyUuid, true, [], 30, 0).pipe(
            map((data) => {
              const reducedByRank = reduceCompanyRolesByRank(data.elements);
              return sortCompanyRolesByRank(reducedByRank);
            }),
            finalize(() => {
              if (this._reportLoadingStateService) {
                this._reportLoadingStateService.setLoadingState(this._companyRolesLoadingId(), false);
              }
            }),
          );

        this.company$ = this._companyService.companyCache.get(companyUuid).pipe(
          finalize(() => {
            if (this._reportLoadingStateService) {
              this._reportLoadingStateService.setLoadingState(this._companyLoadingId(), false);
            }
          }),
        );
      }
    });
  }
}
